// 유튜브 다운로드 + AI 구간 요약 — 링크를 넣으면 자동으로 맥락/구간별 요약을 만들고,
// 구간 시간을 누르면 임베드 영상이 해당 초로 점프. 다운로드는 별도 버튼.
import { useEffect, useRef, useState } from "react";
import { api } from "../../shared/api/client";
import { toast } from "../../shared/stores/toasts";

const isYt = (u: string) => /(youtube\.com|youtu\.be)/i.test(u);
function videoId(url: string): string | null {
  const m = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/|live\/)|youtu\.be\/)([A-Za-z0-9_-]{11})/);
  return m ? m[1] : null;
}
const pad = (n: number) => String(n).padStart(2, "0");
function fmtT(s: number) {
  const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60), sec = Math.floor(s % 60);
  return h > 0 ? `${h}:${pad(m)}:${pad(sec)}` : `${m}:${pad(sec)}`;
}

async function errText(e: any): Promise<string> {
  const d = e?.response?.data;
  if (typeof d === "string") return d;
  if (d?.detail) return d.detail;
  try { const t = await d?.text?.(); if (t) { const j = JSON.parse(t); return j.detail || j?.error?.message || ""; } } catch { /* ignore */ }
  return "";
}

type Segment = { time: number; label: string; detail: string };
type Summary = { title: string; overview: string; has_captions: boolean; segments: Segment[] };

export default function YoutubePage() {
  const [url, setUrl] = useState("");
  const [downloading, setDownloading] = useState(false);
  const [dlPct, setDlPct] = useState(0);
  const [summarizing, setSummarizing] = useState(false);
  const [summary, setSummary] = useState<Summary | null>(null);
  const [sumErr, setSumErr] = useState("");
  const [playAt, setPlayAt] = useState<number | null>(null);
  const lastSum = useRef("");

  const vid = videoId(url.trim());

  const doSummary = async (u: string) => {
    lastSum.current = u;
    setSummarizing(true); setSummary(null); setSumErr(""); setPlayAt(null);
    try {
      const r = await api.post<Summary>("/youtube/summary", { url: u }, { timeout: 180000 });
      setSummary(r.data);
    } catch (e: any) {
      setSumErr((await errText(e)) || "요약에 실패했습니다");
    } finally { setSummarizing(false); }
  };

  // 링크가 들어오면 자동으로 요약 (입력 디바운스 0.7초)
  useEffect(() => {
    const u = url.trim();
    if (!isYt(u) || !videoId(u) || lastSum.current === u) return;
    const t = setTimeout(() => { void doSummary(u); }, 700);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [url]);

  const doDownload = async () => {
    const u = url.trim();
    if (!isYt(u)) { toast.error("유튜브 링크가 아닙니다", "youtube.com / youtu.be 주소를 입력하세요"); return; }
    setDownloading(true); setDlPct(0);
    try {
      const res = await api.post("/youtube/download", { url: u }, {
        responseType: "blob", timeout: 0,
        onDownloadProgress: (e) => { if (e.total) setDlPct(Math.round((e.loaded / e.total) * 100)); },
      });
      const cd = String(res.headers["content-disposition"] || "");
      let name = "youtube.mp4";
      const m = cd.match(/filename\*=UTF-8''([^;]+)/i);
      if (m) { try { name = decodeURIComponent(m[1]); } catch { /* keep default */ } }
      const blobUrl = URL.createObjectURL(res.data as Blob);
      const a = document.createElement("a"); a.href = blobUrl; a.download = name; a.click();
      URL.revokeObjectURL(blobUrl);
      toast.success("다운로드 완료", name);
    } catch (e: any) {
      toast.error("다운로드 실패", await errText(e));
    } finally { setDownloading(false); setDlPct(0); }
  };

  const paste = async () => {
    try {
      const txt = ((await navigator.clipboard.readText()) || "").trim();
      if (!isYt(txt)) { toast.error("클립보드에 유튜브 링크가 없습니다", "유튜브 주소를 복사한 뒤 눌러주세요"); return; }
      setUrl(txt);   // 자동 요약 트리거
    } catch { toast.error("클립보드 접근 실패", "브라우저 클립보드 권한을 허용하세요"); }
  };

  const activeIdx = summary && playAt != null
    ? summary.segments.reduce((acc, s, i) => (s.time <= playAt! ? i : acc), -1)
    : -1;

  const playerSrc = vid
    ? `https://www.youtube.com/embed/${vid}?rel=0${playAt != null ? `&start=${playAt}&autoplay=1` : ""}`
    : "";

  return (
    <div style={{ height: "100%", overflowY: "auto" }}>
      <div style={{ maxWidth: 880, margin: "0 auto", padding: "28px 20px 48px" }}>
        {/* Header */}
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 18 }}>
          <span style={{ fontSize: 26 }}>📺</span>
          <div>
            <h2 style={{ margin: 0, fontSize: 19, fontWeight: 800, color: "var(--text-1)" }}>유튜브 다운로드 · 구간 요약</h2>
            <p style={{ margin: "2px 0 0", fontSize: 12.5, color: "var(--text-3)" }}>링크를 넣으면 자동으로 내용을 구간별로 정리합니다. 시간을 누르면 그 장면으로 이동해요 (Gemini 3.5 Flash)</p>
          </div>
        </div>

        {/* Input + actions */}
        <div className="t-card" style={{ padding: 16 }}>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
            <input
              className="t-input"
              style={{ flex: 1, minWidth: 240, fontSize: 14 }}
              placeholder="https://www.youtube.com/watch?v=…  또는  https://youtu.be/…"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
            />
            <button className="t-btn" onClick={paste} disabled={downloading} title="클립보드의 유튜브 링크 붙여넣기">📋 붙여넣기</button>
            <button className="t-btn t-btn-primary" onClick={doDownload} disabled={downloading || !url.trim()}>
              ⬇️ {downloading ? "받는 중…" : "다운로드"}
            </button>
          </div>

          {downloading && (
            <div style={{ marginTop: 14 }}>
              <div style={{ fontSize: 12.5, color: "var(--text-2)", marginBottom: 6 }}>
                {dlPct > 0 ? `내려받는 중… ${dlPct}%` : "서버에서 영상 준비 중… (길이에 따라 시간이 걸립니다)"}
              </div>
              <div style={{ height: 8, borderRadius: 4, background: "var(--border)", overflow: "hidden" }}>
                <div style={{
                  width: dlPct > 0 ? `${dlPct}%` : "30%", height: "100%", borderRadius: 4,
                  background: "linear-gradient(90deg, var(--p-400,#818cf8), var(--p-600,#4f46e5))",
                  transition: "width .3s", animation: dlPct > 0 ? undefined : "ytpulse 1.2s ease-in-out infinite",
                }} />
              </div>
            </div>
          )}
        </div>

        {/* Player + segments */}
        {vid && (
          <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1.3fr) minmax(280px, 1fr)", gap: 16, marginTop: 16, alignItems: "start" }}>
            {/* Player */}
            <div>
              <div style={{ borderRadius: 12, overflow: "hidden", border: "1px solid var(--border)", aspectRatio: "16 / 9", background: "#000" }}>
                <iframe
                  key={`${vid}-${playAt ?? "x"}`}
                  title="youtube-player"
                  src={playerSrc}
                  style={{ width: "100%", height: "100%", border: 0 }}
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              </div>
              {summary?.overview && (
                <div className="t-card" style={{ padding: 14, marginTop: 12 }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: "var(--text-3)", marginBottom: 5 }}>한 줄 요약</div>
                  <div style={{ fontSize: 13.5, lineHeight: 1.7, color: "var(--text-1)" }}>{summary.overview}</div>
                </div>
              )}
            </div>

            {/* Segment list */}
            <div className="t-card" style={{ padding: 0, overflow: "hidden", maxHeight: "70vh", display: "flex", flexDirection: "column" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "12px 14px", borderBottom: "1px solid var(--border)" }}>
                <span style={{ fontSize: 16 }}>🧠</span>
                <span style={{ fontWeight: 700, fontSize: 13.5, color: "var(--text-1)", flex: 1 }}>구간별 요약</span>
                <span style={{ fontSize: 10.5, color: "var(--text-3)" }}>Gemini 3.5 Flash</span>
              </div>

              <div style={{ flex: 1, overflowY: "auto" }}>
                {summarizing && (
                  <div style={{ padding: "26px 16px", textAlign: "center", color: "var(--text-3)", fontSize: 13 }}>
                    ⏳ 자막을 분석해 구간별로 정리하는 중…<br /><span style={{ fontSize: 11.5 }}>수십 초 걸릴 수 있어요</span>
                  </div>
                )}
                {!summarizing && sumErr && (
                  <div style={{ padding: "20px 16px", color: "var(--text-2)", fontSize: 12.5, lineHeight: 1.6 }}>
                    <div style={{ color: "#ef4444", fontWeight: 600, marginBottom: 4 }}>요약 실패</div>
                    {sumErr}
                    <button className="t-btn" style={{ marginTop: 10, fontSize: 12 }} onClick={() => doSummary(url.trim())}>다시 시도</button>
                  </div>
                )}
                {!summarizing && summary && summary.segments.length === 0 && (
                  <div style={{ padding: "20px 16px", color: "var(--text-3)", fontSize: 12.5, lineHeight: 1.6 }}>
                    이 영상은 자막이 없어 구간 분할은 못 했어요. 위의 한 줄 요약을 참고하세요.
                  </div>
                )}
                {!summarizing && summary?.segments.map((s, i) => (
                  <div
                    key={i}
                    onClick={() => setPlayAt(s.time)}
                    style={{
                      display: "flex", gap: 10, padding: "10px 14px", cursor: "pointer",
                      borderBottom: "1px solid var(--border)",
                      background: i === activeIdx ? "rgba(99,102,241,0.10)" : "transparent",
                    }}
                    onMouseEnter={(e) => { if (i !== activeIdx) e.currentTarget.style.background = "var(--hover)"; }}
                    onMouseLeave={(e) => { if (i !== activeIdx) e.currentTarget.style.background = "transparent"; }}
                  >
                    <span style={{
                      flexShrink: 0, alignSelf: "flex-start", fontSize: 11.5, fontWeight: 700,
                      color: "var(--p-600,#4f46e5)", background: "var(--panel)", border: "1px solid var(--border)",
                      borderRadius: 6, padding: "2px 7px", fontFamily: "var(--font-mono, monospace)",
                    }}>▶ {fmtT(s.time)}</span>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontSize: 13, fontWeight: 600, color: "var(--text-1)" }}>{s.label}</div>
                      {s.detail && <div style={{ fontSize: 12, color: "var(--text-2)", lineHeight: 1.55, marginTop: 2 }}>{s.detail}</div>}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>

      <style>{`@keyframes ytpulse { 0%,100% { opacity: .55 } 50% { opacity: 1 } }`}</style>
    </div>
  );
}
